import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import React from 'react';
import { ReactComponent as FooterLogo } from '../../assets/alliance/footer-logo.svg';
import {
  ALLIANCE_APP_ORDER,
  ALLIANCE_APPS,
  AllianceAppKey,
} from './alliance-apps';
import {
  AllianceLink,
  AllianceSocialLink,
  copyrightText,
  FOOTER_COLUMNS,
  FOOTER_SOCIAL_LINKS,
  LEGAL_LINKS,
} from './alliance-links';
import { useIsMobileLayout } from './bar-controls';
import { CURRENT_APP } from './current-app';

/**
 * The footer is always its own ink band, the same on light and dark pages
 * (Claude Design Footer spec).
 */
const FOOTER_COLORS = {
  background: '#0e0f10',
  border: '#ffffff14',
  heading: '#ffffff99',
  link: '#ffffffcc',
  muted: '#ffffff99',
  faint: '#ffffff80',
  appLink: '#ffffffb3',
  socialBorder: '#ffffff26',
};

/** Hover color for each app's name in the app links row */
const APP_LINK_HOVER: Record<AllianceAppKey, string> = {
  arc: '#00baff',
  contracting: '#00C868',
  leads: '#33aaff',
  events: '#ff6f60',
  university: '#ff7085',
};

/**
 * footer-logo.svg is a white disc (first path) with an unfilled "A" on it
 * (the SVG root is fill="none"); paint the "A" in the footer's ink.
 */
function FooterMark(props: { size: number }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flex: 'none',
        '& path:not(:first-of-type)': { fill: FOOTER_COLORS.background },
      }}
    >
      <FooterLogo
        width={props.size}
        height={props.size}
        aria-label="The Alliance"
      />
    </Box>
  );
}

const linkProps = (params: { href: string }) => {
  const external = /^https?:/.test(params.href);
  return {
    href: params.href,
    target: external ? '_blank' : undefined,
    rel: external ? 'noopener noreferrer' : undefined,
  };
};

/** "ARC · CONTRACTING · …": the current app in white, the others muted */
function AppLinksNav(props: { hideCurrent?: boolean }) {
  return (
    <Box
      component="nav"
      aria-label="Alliance apps"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: props.hideCurrent ? '6px 16px' : '6px 20px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.12em',
        textTransform: 'uppercase',
      }}
    >
      {ALLIANCE_APP_ORDER.filter(
        (key) => !(props.hideCurrent && key === CURRENT_APP.key),
      ).map((key) => {
        const isCurrent = key === CURRENT_APP.key;
        return (
          <Box
            key={key}
            component="a"
            {...linkProps({ href: ALLIANCE_APPS[key].url })}
            aria-current={isCurrent ? 'page' : undefined}
            sx={{
              color: isCurrent ? '#ffffff' : FOOTER_COLORS.appLink,
              textDecoration: 'none',
              transition: 'color 0.15s',
              '&:hover': { color: isCurrent ? '#ffffff' : APP_LINK_HOVER[key] },
            }}
          >
            {ALLIANCE_APPS[key].name}
          </Box>
        );
      })}
    </Box>
  );
}

/** Square social buttons: 36px on desktop, 44px touch targets on phones */
function SocialLinks(props: { size: number }) {
  return (
    <Box sx={{ display: 'flex', gap: props.size > 40 ? '4px' : 1 }}>
      {FOOTER_SOCIAL_LINKS.map((social: AllianceSocialLink) => (
        <Box
          key={social.label}
          component="a"
          {...linkProps({ href: social.href })}
          title={social.label}
          aria-label={social.label}
          sx={{
            width: props.size,
            height: props.size,
            boxSizing: 'border-box',
            borderRadius: '10px',
            border: `1px solid ${FOOTER_COLORS.socialBorder}`,
            color: FOOTER_COLORS.link,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 0.15s, border-color 0.15s',
            '&:hover': {
              color: social.hover.dark,
              borderColor: `${social.hover.dark}99`,
            },
            '&:focus-visible': {
              outline: '2px solid #ffffff',
              outlineOffset: 2,
            },
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d={social.iconPath} />
          </svg>
        </Box>
      ))}
    </Box>
  );
}

/** One footer link: 14px on desktop, a 40px row on phones */
function FooterLink(props: {
  link: AllianceLink;
  strong?: boolean;
  mobile?: boolean;
}) {
  return (
    <Box
      component="a"
      {...linkProps({ href: props.link.href })}
      sx={{
        color: FOOTER_COLORS.link,
        fontSize: props.mobile ? 15 : 14,
        lineHeight: 1.35,
        fontWeight: props.strong ? 500 : 400,
        textDecoration: 'none',
        width: 'fit-content',
        ...(props.mobile
          ? { minHeight: 40, display: 'flex', alignItems: 'center' }
          : {}),
        '&:hover': { color: '#ffffff', textDecoration: 'underline' },
      }}
    >
      {props.link.label}
    </Box>
  );
}

/** Privacy · Terms · Do Not Sell */
function LegalLinks(props: { mobile?: boolean }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: props.mobile ? '4px 18px' : '8px 20px',
      }}
    >
      {LEGAL_LINKS.map((link) => (
        <Box
          key={link.label}
          component="a"
          {...linkProps({ href: link.href })}
          sx={{
            color: FOOTER_COLORS.muted,
            textDecoration: 'none',
            ...(props.mobile
              ? { minHeight: 32, display: 'flex', alignItems: 'center' }
              : {}),
            '&:hover': { color: '#ffffff', textDecoration: 'underline' },
          }}
        >
          {link.label}
        </Box>
      ))}
    </Box>
  );
}

/** Phones: a 52px heading row that expands its links (one open at a time) */
function FooterSection(props: {
  title: string;
  links: AllianceLink[];
  open: boolean;
  onToggle: () => void;
}) {
  const id = `footer-${props.title.toLowerCase()}`;
  return (
    <Box sx={{ borderBottom: `1px solid ${FOOTER_COLORS.border}` }}>
      <ButtonBase
        onClick={props.onToggle}
        aria-expanded={props.open ? 'true' : 'false'}
        aria-controls={id}
        sx={{
          fontFamily: 'inherit',
          width: '100%',
          height: 52,
          justifyContent: 'space-between',
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          color: '#ffffffe6',
        }}
      >
        {props.title}
        <ExpandMoreIcon
          sx={{
            fontSize: 22,
            color: FOOTER_COLORS.muted,
            transform: props.open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        />
      </ButtonBase>
      <Collapse in={props.open} timeout="auto">
        <Box
          id={id}
          sx={{ display: 'flex', flexDirection: 'column', pb: '10px' }}
        >
          {props.links.map((link, index) => (
            <FooterLink
              key={link.label}
              link={link}
              strong={props.title === 'Connect' && index === 0}
              mobile
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * The Alliance footer: an ink band with the "A" logo, the five app links,
 * social buttons, the Connect / Tours / About / Other columns, the legal
 * links and the copyright line.
 *
 * Desktop: logo · app links · social in one row, a 4-column grid, then the
 * legal row. Below md: logo and social, the other apps, the columns as
 * collapsible rows (Connect open, one open at a time), legal and copyright.
 */
export function AllianceFooter() {
  // State
  const [openSection, setOpenSection] = React.useState<string | null>(
    FOOTER_COLUMNS[0].title,
  );
  // Hooks
  const isMobile = useIsMobileLayout();

  return (
    <Box
      component="footer"
      sx={{
        width: '100%',
        backgroundColor: FOOTER_COLORS.background,
        borderTop: `1px solid ${FOOTER_COLORS.border}`,
        color: FOOTER_COLORS.appLink,
        fontFamily: 'Roboto, sans-serif',
        letterSpacing: 'normal',
        boxSizing: 'border-box',
      }}
    >
      {isMobile ? (
        <Box
          sx={{
            p: '28px 20px 24px',
            // Keep the copyright clear of the buttons fixed to the
            // bottom-left corner: CookieYes consent (15px up) and, in ARC,
            // back-to-top (75px up)
            pb: '128px',
            display: 'flex',
            flexDirection: 'column',
            gap: '22px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FooterMark size={44} />
            <Box sx={{ flex: 1 }} />
            <SocialLinks size={44} />
          </Box>

          <AppLinksNav hideCurrent />

          <Box sx={{ borderTop: `1px solid ${FOOTER_COLORS.border}` }}>
            {FOOTER_COLUMNS.map((column) => (
              <FooterSection
                key={column.title}
                title={column.title}
                links={column.links}
                open={openSection === column.title}
                onToggle={() =>
                  setOpenSection((current) =>
                    current === column.title ? null : column.title,
                  )
                }
              />
            ))}
          </Box>

          <Box sx={{ fontSize: 12 }}>
            <LegalLinks mobile />
          </Box>

          <Box sx={{ fontSize: 12, color: FOOTER_COLORS.faint }}>
            {copyrightText()}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            p: '40px 48px 28px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <FooterMark size={48} />
            <Box
              sx={{
                width: '1px',
                height: 28,
                backgroundColor: FOOTER_COLORS.socialBorder,
              }}
            />
            <AppLinksNav />
            <Box sx={{ flex: 1 }} />
            <SocialLinks size={36} />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
              gap: 4,
              pt: 4,
              borderTop: `1px solid ${FOOTER_COLORS.border}`,
            }}
          >
            {FOOTER_COLUMNS.map((column) => (
              <Box
                key={column.title}
                sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
              >
                <Box
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: FOOTER_COLORS.heading,
                  }}
                >
                  {column.title}
                </Box>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
                >
                  {column.links.map((link, index) => (
                    <FooterLink
                      key={link.label}
                      link={link}
                      strong={column.title === 'Connect' && index === 0}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '10px 24px',
              pt: '20px',
              borderTop: `1px solid ${FOOTER_COLORS.border}`,
              fontSize: 12,
            }}
          >
            <LegalLinks />
            <Box sx={{ flex: 1 }} />
            <Box sx={{ color: FOOTER_COLORS.faint }}>{copyrightText()}</Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
